import React, { useMemo, useState } from "react";
import { Plus, UserCog } from "lucide-react";
import type { Resource, RoleResource, VmsRole } from "../../../features/rbac/types";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { EmptyState } from "../shared/EmptyState";
import { FormModal, LabeledField, TextInput } from "../shared/FormModal";
import { RowActions } from "../shared/RowActions";
import { SearchInput } from "../shared/SearchInput";
import { SectionCard } from "../shared/SectionCard";
import { StatusPill } from "../shared/StatusPill";
import { useRbacAction } from "../hooks/useRbacAction";

interface RoleResourcesTabProps {
  roleResources: RoleResource[];
  vmsRoles: VmsRole[];
  resources: Resource[];
  onCreate: (payload: { role: string; description?: string }) => void;
  onUpdate: (payload: { roleResourceId: number; data: Record<string, unknown> }) => void;
  onDelete: (roleResourceId: number) => void;
}

interface RRFormState {
  role: string;
  description: string;
}

const emptyForm: RRFormState = { role: "", description: "" };

export const RoleResourcesTab: React.FC<RoleResourcesTabProps> = ({
  roleResources,
  vmsRoles,
  resources,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const { matches, isBusy } = useRbacAction();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<RoleResource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoleResource | null>(null);
  const [form, setForm] = useState<RRFormState>(emptyForm);

  const roleNameById = useMemo(
    () => new Map(vmsRoles.map((r) => [r.roleId, r.role])),
    [vmsRoles]
  );
  const resourceNameById = useMemo(
    () => new Map(resources.map((r) => [r.resourceId, r.resourceName])),
    [resources]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roleResources;
    return roleResources.filter((rr) => {
      const resourceName = resourceNameById.get(rr.resourceId) ?? "";
      const roleName = roleNameById.get(rr.roleId) ?? "";
      return (
        rr.role.toLowerCase().includes(q) ||
        rr.description.toLowerCase().includes(q) ||
        resourceName.toLowerCase().includes(q) ||
        roleName.toLowerCase().includes(q) ||
        String(rr.roleResourceId).includes(q)
      );
    });
  }, [roleResources, roleNameById, resourceNameById, search]);

  const openCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const openEdit = (rr: RoleResource) => {
    setForm({ role: rr.role, description: rr.description ?? "" });
    setEditTarget(rr);
  };

  const closeForm = () => {
    setCreateOpen(false);
    setEditTarget(null);
    setForm(emptyForm);
  };

  const trimmed = {
    role: form.role.trim(),
    description: form.description.trim(),
  };

  const submitDisabled = !trimmed.role;

  const handleSubmit = () => {
    if (submitDisabled) return;
    if (editTarget) {
      const patch: Record<string, unknown> = {};
      if (trimmed.role !== editTarget.role) patch.role = trimmed.role;
      if (trimmed.description !== (editTarget.description ?? ""))
        patch.description = trimmed.description;
      if (Object.keys(patch).length === 0) {
        closeForm();
        return;
      }
      onUpdate({ roleResourceId: editTarget.roleResourceId, data: patch });
    } else {
      onCreate(
        trimmed.description
          ? { role: trimmed.role, description: trimmed.description }
          : { role: trimmed.role }
      );
    }
    closeForm();
  };

  return (
    <SectionCard
      title="Role-resources"
      description="Sub-roles defined under your authenticated role-resource context."
      actions={
        <>
          <SearchInput value={search} onChange={setSearch} placeholder="Search role-resources" />
          <button
            type="button"
            onClick={openCreate}
            disabled={isBusy}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            New role-resource
          </button>
        </>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState
          icon={<UserCog className="h-5 w-5" />}
          title={
            search ? "No role-resources match your search" : "No role-resources yet"
          }
          description={
            search
              ? "Try a different search term."
              : "Create your first sub-role. Hierarchy is resolved from the authenticated user context."
          }
          action={
            !search && (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90"
              >
                <Plus className="h-4 w-4" />
                New role-resource
              </button>
            )
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-cardborder bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">VMS role</th>
                <th className="px-4 py-2">Resource</th>
                <th className="px-4 py-2">Parent</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((rr) => (
                <tr key={rr.roleResourceId} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{rr.roleResourceId}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{rr.role}</td>
                  <td className="px-4 py-3 text-gray-600">{rr.description || "—"}</td>
                  <td className="px-4 py-3">
                    <StatusPill variant="info">
                      {rr.roleId} · {roleNameById.get(rr.roleId) ?? "unknown"}
                    </StatusPill>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {rr.resourceId} · {resourceNameById.get(rr.resourceId) ?? "unknown"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {rr.parentId == null ? <StatusPill variant="warning">root</StatusPill> : rr.parentId}
                  </td>
                  <td className="px-4 py-3">
                    <RowActions
                      onEdit={() => openEdit(rr)}
                      onDelete={() => setDeleteTarget(rr)}
                      editing={matches("updateRoleResource", rr.roleResourceId)}
                      deleting={matches("deleteRoleResource", rr.roleResourceId)}
                      disabled={isBusy}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FormModal
        open={createOpen || editTarget != null}
        title={editTarget ? `Edit role-resource · ${editTarget.role}` : "Create new role-resource"}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={
          editTarget
            ? matches("updateRoleResource", editTarget.roleResourceId)
            : matches("createRoleResource")
        }
        submitDisabled={submitDisabled}
        submitLabel={editTarget ? "Save changes" : "Create role-resource"}
      >
        <LabeledField
          label="Role name"
          required
          hint="Parent role / resource is derived from your authenticated context."
        >
          <TextInput
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            placeholder="e.g. operations-manager"
            autoFocus
          />
        </LabeledField>
        <LabeledField label="Description">
          <TextInput
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Short description"
          />
        </LabeledField>
      </FormModal>

      <ConfirmDialog
        open={deleteTarget != null}
        title={`Delete role-resource "${deleteTarget?.role ?? ""}"?`}
        description="Users and mappings that point at this role-resource may break."
        confirmLabel="Delete role-resource"
        destructive
        loading={
          deleteTarget ? matches("deleteRoleResource", deleteTarget.roleResourceId) : false
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            onDelete(deleteTarget.roleResourceId);
            setDeleteTarget(null);
          }
        }}
      />
    </SectionCard>
  );
};
